import React, { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Upload, Download, FileText, CheckCircle2, XCircle, AlertCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LEAD_TEMPLATE = {
  headers: [
    "name", "phone", "branch", "source", "email",
    "fitness_goal", "program_interest", "budget", "notes",
    "assigned_to_email", "first_name", "last_name", "age_group",
    "gender", "referred_by_phone",
  ],
  example: [
    "Aarav Sharma", "9876543210", "Indiranagar", "WEBSITE", "aarav@example.com",
    "WEIGHT_LOSS", "STRENGTH", "3500", "Interested in evening slot",
    "coach@nammaxfit.com", "Aarav", "Sharma", "25-34", "M", "",
  ],
};

const MEMBER_TEMPLATE = {
  headers: [
    "full_name", "phone", "email", "branch", "plan_name", "category",
    "price", "start_date", "duration_days", "total_sessions", "coach_email",
  ],
  example: [
    "Neha Rao", "9012345678", "neha@example.com", "Indiranagar", "Group 3-Month",
    "GROUP_TRAINING", "9000", "2026-02-15", "90", "0", "coach@nammaxfit.com",
  ],
};

// Minimal CSV parser — handles quoted fields, commas inside quotes, and \r\n.
function parseCSV(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') { inQ = false; }
      else { field += ch; }
    } else if (ch === '"') {
      inQ = true;
    } else if (ch === ",") {
      cur.push(field); field = "";
    } else if (ch === "\n") {
      cur.push(field); rows.push(cur); cur = []; field = "";
    } else if (ch === "\r") {
      // ignore
    } else {
      field += ch;
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  return rows.filter((r) => r.some((c) => (c || "").trim().length > 0));
}

function toCSV(headers, exampleRow) {
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return headers.join(",") + "\n" + exampleRow.map(esc).join(",") + "\n";
}

function downloadCSV(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = filename;
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function coerceRow(headers, values, kind) {
  const obj = {};
  headers.forEach((h, i) => { obj[h] = (values[i] ?? "").trim(); });
  if (kind === "leads") {
    if (obj.budget) obj.budget = Number(obj.budget);
    else delete obj.budget;
  } else {
    obj.price = Number(obj.price || 0);
    obj.duration_days = Number(obj.duration_days || 90);
    obj.total_sessions = Number(obj.total_sessions || 0);
  }
  // strip empty optional strings so backend defaults apply
  Object.keys(obj).forEach((k) => {
    if (obj[k] === "" || obj[k] === undefined) delete obj[k];
  });
  return obj;
}

function ImportPanel({ kind, template, endpoint, title, subtitle, testidPrefix }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [parseErr, setParseErr] = useState("");
  const fileRef = useRef(null);
  const qc = useQueryClient();

  const onFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    setResult(null); setParseErr("");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = parseCSV(String(reader.result || ""));
        if (raw.length < 1) throw new Error("Empty CSV");
        const headers = raw[0].map((h) => h.trim().toLowerCase());
        const required = template.headers.slice(0, kind === "leads" ? 3 : 6);
        const missing = required.filter((r) => !headers.includes(r));
        if (missing.length > 0) throw new Error(`Missing required columns: ${missing.join(", ")}`);
        const parsed = raw.slice(1).map((r) => coerceRow(headers, r, kind));
        setRows(parsed);
      } catch (e) {
        setRows([]);
        setParseErr(e.message || "Failed to parse CSV");
      }
    };
    reader.readAsText(file);
  };

  const submit = useMutation({
    mutationFn: async () => (await apiClient.post(endpoint, { rows })).data,
    onSuccess: (d) => {
      setResult(d);
      qc.invalidateQueries({ queryKey: [kind === "leads" ? "leads" : "members"] });
      qc.invalidateQueries({ queryKey: ["members"] });
      toast.success(`${d.created} imported, ${d.failed} failed`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Import failed"),
  });

  const preview = useMemo(() => rows.slice(0, 10), [rows]);

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-semibold">{subtitle}</div>
          <h2 className="font-display text-2xl font-black uppercase tracking-tight mt-1">{title}</h2>
        </div>
        <Button
          variant="outline"
          onClick={() => downloadCSV(`nammaxfit_${kind}_template.csv`, toCSV(template.headers, template.example))}
          data-testid={`${testidPrefix}-download-template`}
          className="bg-zinc-900 border-zinc-800 hover:border-volt text-volt uppercase tracking-wider rounded-sm">
          <Download size={14} className="mr-1" /> Download Template
        </Button>
      </div>

      <div
        className="border-2 border-dashed border-zinc-800 rounded-sm p-8 grid place-items-center bg-zinc-950 hover:border-volt/50 transition-colors cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
        data-testid={`${testidPrefix}-dropzone`}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid={`${testidPrefix}-file-input`}
        />
        <Upload size={28} className="text-volt mb-2" />
        <div className="text-sm text-zinc-300">Drop a CSV file here or <span className="text-volt font-semibold">click to browse</span></div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-2">
          Required columns: <span className="text-zinc-300">{template.headers.slice(0, kind === "leads" ? 3 : 6).join(", ")}</span>
        </div>
        {fileName && (
          <div className="text-xs text-zinc-400 mt-3 flex items-center gap-2">
            <FileText size={12} /> {fileName} · {rows.length} rows
          </div>
        )}
      </div>

      {parseErr && (
        <div className="bg-red-950/40 border border-red-900 rounded-sm p-3 flex items-start gap-2 text-sm text-red-300" data-testid={`${testidPrefix}-parse-error`}>
          <AlertCircle size={16} className="mt-0.5" /> {parseErr}
        </div>
      )}

      {preview.length > 0 && (
        <Panel>
          <PanelHeader subtitle={`Preview (first ${preview.length} of ${rows.length})`} title="Rows to import" />
          <div className="overflow-x-auto scrollbar-thin">
            <table className="min-w-full text-xs" data-testid={`${testidPrefix}-preview-table`}>
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  <th className="text-left p-2 border-b border-zinc-900">#</th>
                  {template.headers.map((h) => (
                    <th key={h} className="text-left p-2 border-b border-zinc-900">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={i} className="text-zinc-300">
                    <td className="p-2 border-b border-zinc-900 text-zinc-600">{i + 1}</td>
                    {template.headers.map((h) => (
                      <td key={h} className="p-2 border-b border-zinc-900 whitespace-nowrap">
                        {String(r[h] ?? "").slice(0, 40)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end mt-4">
            <Button
              disabled={submit.isPending || rows.length === 0}
              onClick={() => submit.mutate()}
              data-testid={`${testidPrefix}-submit`}
              className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
              <Upload size={14} className="mr-1" /> {submit.isPending ? "Importing…" : `Import ${rows.length} rows`}
            </Button>
          </div>
        </Panel>
      )}

      {result && (
        <Panel>
          <PanelHeader subtitle="Result" title="Import summary" />
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Total</div>
              <div className="font-display text-2xl font-black text-zinc-100 mt-1">{result.total}</div>
            </div>
            <div className="bg-emerald-950/30 border border-emerald-900/50 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-500 flex items-center gap-1"><CheckCircle2 size={10} /> Created</div>
              <div className="font-display text-2xl font-black text-emerald-400 mt-1" data-testid={`${testidPrefix}-created-count`}>{result.created}</div>
            </div>
            <div className="bg-red-950/30 border border-red-900/50 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-red-400 flex items-center gap-1"><XCircle size={10} /> Failed</div>
              <div className="font-display text-2xl font-black text-red-400 mt-1" data-testid={`${testidPrefix}-failed-count`}>{result.failed}</div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">Errors</div>
              <div className="max-h-60 overflow-y-auto scrollbar-thin space-y-1" data-testid={`${testidPrefix}-error-list`}>
                {result.errors.map((er, i) => (
                  <div key={i} className="text-xs text-red-300 bg-red-950/20 border border-red-900/40 rounded-sm px-2 py-1">
                    Row {er.row}: {er.message}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}

export default function BulkImportPage() {
  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="bulk-import-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Data Import</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Bulk CSV Import</h1>
        <div className="text-zinc-500 text-sm mt-1">Upload leads or members in bulk. Branch can be an ID or name.</div>
      </div>

      <Tabs defaultValue="leads" className="space-y-4">
        <TabsList className="bg-zinc-950 border border-zinc-900" data-testid="bulk-import-tabs">
          <TabsTrigger value="leads" data-testid="tab-leads" className="data-[state=active]:bg-volt data-[state=active]:text-black">Leads</TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members" className="data-[state=active]:bg-volt data-[state=active]:text-black">Members</TabsTrigger>
        </TabsList>
        <TabsContent value="leads">
          <ImportPanel
            kind="leads"
            template={LEAD_TEMPLATE}
            endpoint="/leads/bulk-import"
            title="Import Leads"
            subtitle="Sales CRM"
            testidPrefix="leads-import"
          />
        </TabsContent>
        <TabsContent value="members">
          <ImportPanel
            kind="members"
            template={MEMBER_TEMPLATE}
            endpoint="/members/bulk-import"
            title="Import Members + Memberships"
            subtitle="Membership Roster"
            testidPrefix="members-import"
          />
        </TabsContent>
      </Tabs>

      <DangerZone />
    </div>
  );
}

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);
  const qc = useQueryClient();

  const reset = useMutation({
    mutationFn: async () => (await apiClient.post("/admin/reset-test-data", { confirm: "WIPE_TEST_DATA" })).data,
    onSuccess: (d) => {
      setResult(d);
      setConfirmText("");
      qc.invalidateQueries();
      toast.success("Test data reset complete");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  const phraseOk = confirmText === "WIPE_TEST_DATA";

  return (
    <Panel className="border-red-900/50 bg-red-950/10" data-testid="danger-zone-panel">
      <PanelHeader subtitle="Danger Zone" title="Reset test data" />
      <div className="text-sm text-zinc-300 space-y-2">
        <p>
          Deletes <span className="text-red-300 font-semibold">ALL</span> leads, members
          (except the demo customer), memberships, attendance, coach tasks, referrals,
          job referrals, REPS ledger, redemptions, payment orders/approvals, expenses,
          merch sales and assessment records.
        </p>
        <p className="text-zinc-500">
          Preserved: demo logins (super / admin / coach / customer @nammaxfit.com),
          branches, plans, session templates, checklist templates, policy documents and
          reward catalog. One <span className="text-volt font-semibold">SAMPLE</span> entry
          is reseeded per category so your team can see the shape of each record.
        </p>
      </div>

      <div className="mt-4 flex justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setConfirmText(""); setResult(null); } }}>
          <DialogTrigger asChild>
            <Button
              data-testid="open-reset-dialog"
              className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wider rounded-sm">
              <Trash2 size={14} className="mr-1" /> Reset Test Data
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0b0b0b] border-red-900" data-testid="reset-dialog">
            <DialogHeader>
              <DialogTitle className="font-display uppercase text-red-400 flex items-center gap-2">
                <AlertCircle size={18} /> Confirm data reset
              </DialogTitle>
              <DialogDescription className="text-zinc-400 text-sm mt-2">
                This action cannot be undone. To proceed, type the phrase
                <span className="mx-1 font-mono-cmd text-volt">WIPE_TEST_DATA</span>
                exactly as shown and click Reset.
              </DialogDescription>
            </DialogHeader>

            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="WIPE_TEST_DATA"
              data-testid="reset-confirm-input"
              className="bg-zinc-950 border-zinc-800 font-mono-cmd"
              autoFocus
            />

            {result && (
              <div className="mt-2 max-h-56 overflow-y-auto scrollbar-thin text-xs" data-testid="reset-result">
                <div className="text-emerald-400 mb-2 flex items-center gap-1"><CheckCircle2 size={12} /> {result.message}</div>
                <div className="grid grid-cols-2 gap-1">
                  {Object.entries(result.wiped || {}).map(([k, v]) => (
                    <div key={k} className="text-zinc-400"><span className="text-zinc-500">{k}:</span> {String(v)}</div>
                  ))}
                </div>
                <div className="text-volt mt-2">Reseeded: {(result.reseeded || []).join(", ")}</div>
              </div>
            )}

            <DialogFooter className="mt-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="reset-cancel"
                className="bg-zinc-900 border-zinc-800">
                Cancel
              </Button>
              <Button
                disabled={!phraseOk || reset.isPending}
                onClick={() => reset.mutate()}
                data-testid="reset-confirm-submit"
                className="bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wider rounded-sm">
                <Trash2 size={14} className="mr-1" /> {reset.isPending ? "Resetting…" : "Reset now"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Panel>
  );
}
